import { doc, getDoc } from "firebase/firestore";
import { db } from "../db/firebase";

export type UserRole = "admin" | "member" | null;

export interface UserAccess {
  role: UserRole;
  isAdmin: boolean;
  approved: boolean;
  organizationId: string | null;
  profile: Record<string, any> | null;
}

/** "organization" predates the admin/member split but is still stored on older org owners. */
const ADMIN_ROLES = ["admin", "organization"];

export const NO_ACCESS: UserAccess = {
  role: null,
  isAdmin: false,
  approved: false,
  organizationId: null,
  profile: null,
};

/**
 * Single source of truth for what a signed-in account is allowed to do.
 * Every guard, redirect and role check reads from here so the rules can't drift.
 */
export async function resolveUserAccess(uid: string): Promise<UserAccess> {
  const userSnap = await getDoc(doc(db, "users", uid));

  if (userSnap.exists()) {
    const data = userSnap.data();
    const isAdmin = ADMIN_ROLES.includes(data.role);

    return {
      role: isAdmin ? "admin" : "member",
      isAdmin,
      approved: data.approved === true,
      organizationId: data.organizationId ?? (isAdmin ? uid : null),
      profile: data,
    };
  }

  // Org owners created before the dual write always have an organizations doc.
  const orgSnap = await getDoc(doc(db, "organizations", uid));
  if (orgSnap.exists() && ADMIN_ROLES.includes(orgSnap.data().role)) {
    return {
      role: "admin",
      isAdmin: true,
      approved: true,
      organizationId: uid,
      profile: orgSnap.data(),
    };
  }

  return NO_ACCESS;
}
